import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchCompatibilities,
  deleteCompatibility,
  deleteCompatibilityBulk,
  sendHonestQuestion,
  closePairKindly,
  fetchIntroductionTimeline,
} from "../services/compatibilityService";
import { showConfirmationDialog, handleUnauthorized } from "../../utils/apiHelper";
import { getModulePermissions } from "../../utils/permissionUtils";
import { useDebounce } from "../../hook/useDebounce";
import {
  boardRowModel,
  boardPanelModel,
  stageStripModel,
  tabsModel,
  headerPills,
  timelineModel,
  emptyStateText,
  bulkResultText,
  buildBoardQuery,
  boardRequestParams,
  initialBoardState,
  boardReducer,
  boardKeyAction,
  sortOptionsFor,
  canActOn,
  firstNameOf,
  sideIdOf,
  legacyVerdict,
  stripOffText,
  statusChipText,
  reverseModel,
  headerSortModel,
  isPhoneWidth,
  PAGE_SIZES,
} from "./introductionsBoardModel";
import "./IntroductionsBoard.css";

// The Introductions board (founder go 2026-10-20, "ok build this for the
// Admin panel"; approved mock-up "Introductions, Tighter"). It replaces
// CompatibilityListLayer on /compatibility-control; that file stays in the
// repo, unused, for one release.
//
// This file only draws. What every row, pill, chip and panel line SAYS is
// worked out in introductionsBoardModel.js (with tests), which takes the
// Waiting on and Next words from the helpers the founder approved
// (compatibilityRecordUtils waitingOnLines / waitingOnNextParts).
//
// Where everything from the old table went (the mock-up's own note):
//   - emails, tier meaning, Vault counts, stage seen, untouched pile, earlier
//     Ends, Promise prompts: the pair panel (click a pair);
//   - the Promise badge and the chat badge: folded into Next and the journey;
//   - the honest question and Close kindly: labelled panel buttons, plus the
//     bulk bar; Delete and Delete All: Advanced in the panel and the bulk
//     bar, never on a row;
//   - the status filter: the tabs plus the stage strip; Dealt from / to: the
//     Dealt dates control; dealt in 24 h and inventory rules: header pills.
//
// Server: GET /admin/interactions with view / stage / sort / member (the
// 2026-10-20 contract). An older server (no tabCounts in the answer to a
// request that sent a view) gets today's params instead, and the board
// hides what it cannot know.

const HONEST_CONFIRM =
  "Send the honest question to this pair's quiet member? One prompt per member per pair, ever.";
const CLOSE_CONFIRM =
  "Close this introduction kindly? Both members are told, and both go back into rotation. The honest question is sent first if nobody has been prompted yet.";

const TL_STATES = { idle: "idle", loading: "loading", ok: "ok", missing: "missing", error: "error" };

// ---- Small inline icons (no icon font request) -------------------------

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
);
const ClockIcon = ({ className }) => (
  <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const ChevronIcon = ({ left }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d={left ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"} />
  </svg>
);
const ReverseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M7 4v16M3 16l4 4 4-4M17 20V4M13 8l4-4 4 4" />
  </svg>
);
const CloseIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
// One chevron for the title in use (down = descending, and the attention
// ladder), two faint ones for a title that could sort.
const SortArrowIcon = ({ order }) => (
  <svg className="ib-sort-arrow" width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={order === "asc" ? "M2 6.5L5 3.5l3 3" : order ? "M2 3.5L5 6.5l3-3" : "M3 4l2-2 2 2M3 6l2 2 2-2"} />
  </svg>
);

// A column title: a button when it sorts on this tab, plain text when not.
const SortTitle = ({ sort, onSort, children }) => {
  if (!sort) return <span>{children}</span>;
  return (
    <button
      type="button"
      className={`ib-sort-title${sort.active ? " is-active" : ""}`}
      title={sort.title}
      aria-label={sort.title}
      onClick={() => onSort(sort.key)}
    >
      {children}
      <SortArrowIcon order={sort.active ? sort.order || "desc" : null} />
    </button>
  );
};

const dateOf = (ymd) => (ymd ? moment(ymd, "YYYY-MM-DD").toDate() : null);
const ymdOf = (d) => (d ? moment(d).format("YYYY-MM-DD") : "");

// A swal2 confirm is open: its own keys win.
const dialogOpen = () => typeof document !== "undefined" && !!document.querySelector(".swal2-container");

const IntroductionsBoard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [state, dispatch] = useReducer(boardReducer, location.search, initialBoardState);
  const debouncedSearch = useDebounce(state.search, 400);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    total: 0,
    tabCounts: null,
    stageCounts: null,
    promise: null,
    dealing: null,
    liveCapped: false,
  });
  const [loading, setLoading] = useState(true);
  // The last load failed: the list says so instead of a false "nothing here"
  // (review 2026-10-20).
  const [loadError, setLoadError] = useState(false);
  const [legacyServer, setLegacyServer] = useState(false);
  // The pair a Promise action just ran on: { id, closing }. If it then
  // leaves this list, the panel keeps a copy from before the action, so its
  // actions go off and it says where the pair went (review 2026-10-20).
  const [actedOn, setActedOn] = useState(null);
  const [phone, setPhone] = useState(() => isPhoneWidth());
  const [notices, setNotices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [timeline, setTimeline] = useState({ status: TL_STATES.idle, events: [], partial: false, forId: null });
  const [timelineTick, setTimelineTick] = useState(0);

  const searchRef = useRef(null);
  const panelRef = useRef(null);
  const openerRef = useRef(null);
  const rowRefs = useRef([]);
  const lastWrittenRef = useRef(null);
  const requestSeq = useRef(0);
  const panelRowCache = useRef(null);

  // Permissions, exactly as the old table: module "Compatibility Control"
  // OR "Match Control".
  const [permissionChecked, setPermissionChecked] = useState(false);
  const compatibilityPerms = getModulePermissions("Compatibility Control");
  const legacyPerms = getModulePermissions("Match Control");
  const { shouldCheckPermissions } = compatibilityPerms;
  const canEdit = !shouldCheckPermissions || compatibilityPerms.canEdit || legacyPerms.canEdit;
  const canView = !shouldCheckPermissions || compatibilityPerms.canView || legacyPerms.canView;
  const canDelete = !shouldCheckPermissions || compatibilityPerms.canDelete || legacyPerms.canDelete;
  const canSelect = canEdit || canDelete;

  useEffect(() => {
    if (canView) {
      setPermissionChecked(true);
      return;
    }
    navigate("/access-denied");
  }, [canView, navigate]);

  // Notices stay until dismissed (the old 3 s alerts vanished before they
  // could be read, and "press Close kindly again" was lost with them).
  const addNotice = useCallback((kind, text) => {
    if (!text) return;
    setNotices((prev) => [...prev.slice(-3), { id: `${Date.now()}-${Math.random()}`, kind, text }]);
  }, []);
  const dismissNotice = (id) => setNotices((prev) => prev.filter((n) => n.id !== id));

  // ---- URL <-> state ------------------------------------------------------

  const queryString = buildBoardQuery(state);
  useEffect(() => {
    const current = location.search.replace(/^\?/, "");
    lastWrittenRef.current = queryString;
    if (current === queryString) return;
    navigate({ pathname: location.pathname, search: queryString ? `?${queryString}` : "" }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  useEffect(() => {
    // Back, forward, or a link inside the app: the URL wins.
    const current = location.search.replace(/^\?/, "");
    if (current === lastWrittenRef.current) return;
    dispatch({ type: "replace", search: location.search });
  }, [location.search]);

  // ---- Loading ------------------------------------------------------------

  const params = boardRequestParams(state, { search: debouncedSearch, legacyServer });
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    if (!permissionChecked) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    (async () => {
      const sent = JSON.parse(paramsKey);
      try {
        const res = await fetchCompatibilities(sent);
        if (seq !== requestSeq.current) return;
        setLoadError(false);
        const body = (res && res.data) || {};
        const data = Array.isArray(body.data) ? body.data : [];
        setRows(data);
        setMeta({
          total: (body.pagination && body.pagination.total) || 0,
          tabCounts: body.tabCounts || null,
          stageCounts: body.stageCounts || null,
          promise: body.promise || null,
          dealing: body.dealing || null,
          liveCapped: body.liveCapped === true,
        });
        setNow(Date.now());
        dispatch({ type: "rowsLoaded", ids: data.map((r) => String(r._id)) });
        // A server from before the 2026-10-20 contract sends no tabCounts:
        // switch to today's params (status / testOnly) and load again. Only
        // an answer to a request that sent `view` can say so (a ?status=
        // link sends none, and the new server then adds no tabCounts
        // either); the legacy params keep sending `view`, so a server
        // updated mid-visit switches the board back (review 2026-10-20).
        const legacy = legacyVerdict(sent, body);
        if (legacy !== null) setLegacyServer((was) => (was === legacy ? was : legacy));
      } catch (err) {
        if (seq !== requestSeq.current) return;
        if (err && err.response && err.response.status === 401) {
          handleUnauthorized(navigate);
          return;
        }
        setLoadError(true);
        addNotice(
          "error",
          (err && err.response && err.response.data && err.response.data.message) ||
            "The introductions did not load. Check the connection and try again."
        );
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    })();
  }, [permissionChecked, paramsKey, reloadTick, navigate, addNotice]);

  const reload = () => setReloadTick((t) => t + 1);

  // The stage strip is off when the server cannot filter by stage (an older
  // server, or a live set over the cap): a stage left in the state would
  // read as applied over an unfiltered list (review 2026-10-20).
  const stripOff = stripOffText({ legacyServer, liveCapped: meta.liveCapped && state.view === "live" });
  useEffect(() => {
    if (stripOff && state.stage) dispatch({ type: "clearStage" });
  }, [stripOff, state.stage]);

  // Phone width decides whether the panel is a modal sheet.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setPhone(isPhoneWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const nextRunAt = (meta.promise && meta.promise.nextRunAt) || null;
  const models = useMemo(() => rows.map((r) => boardRowModel(r, { now, nextRunAt })), [rows, now, nextRunAt]);
  const visibleIds = useMemo(() => models.map((m) => m.id), [models]);
  const rowById = useMemo(() => new Map(rows.map((r) => [String(r._id), r])), [rows]);

  // A page past the end (rows deleted, a stale link): go to the last page.
  const totalPages = Math.max(1, Math.ceil(meta.total / state.limit));
  useEffect(() => {
    if (!loading && rows.length === 0 && state.page > 1 && meta.total > 0) {
      dispatch({ type: "page", page: totalPages });
    }
  }, [loading, rows.length, state.page, meta.total, totalPages]);

  // ---- The open pair ------------------------------------------------------

  let openRow = state.openId ? rowById.get(state.openId) : null;
  let panelFromCache = false;
  if (openRow) panelRowCache.current = openRow;
  else if (state.openId && panelRowCache.current && String(panelRowCache.current._id) === state.openId) {
    // The row left this list (closed, filtered out): the panel keeps it.
    openRow = panelRowCache.current;
    panelFromCache = true;
  }
  const panel = openRow ? boardPanelModel(openRow, { now, nextRunAt }) : null;
  // A Promise action ran and the pair then left the list: the copy is from
  // before the action, so its actions are off.
  const panelStale = !!(panel && panelFromCache && actedOn && actedOn.id === panel.id);

  useEffect(() => {
    const id = state.openId;
    if (!id) {
      setTimeline({ status: TL_STATES.idle, events: [], partial: false, forId: null });
      return undefined;
    }
    let live = true;
    setTimeline({ status: TL_STATES.loading, events: [], partial: false, forId: id });
    fetchIntroductionTimeline(id)
      .then((res) => {
        if (!live) return;
        const data = (res && res.data && res.data.data) || {};
        // partial: true = a source failed to read, so the story has gaps
        // (review 2026-10-20: it used to look complete).
        setTimeline({
          status: TL_STATES.ok,
          events: Array.isArray(data.events) ? data.events : [],
          partial: data.partial === true,
          forId: id,
        });
      })
      .catch((err) => {
        if (!live) return;
        const code = err && err.response && err.response.status;
        if (code === 401) {
          handleUnauthorized(navigate);
          return;
        }
        // No route yet (an older server answers 404): say so plainly. A
        // network failure (no status) is an error with Try again, never
        // "not available" (review 2026-10-20).
        setTimeline({ status: code === 404 ? TL_STATES.missing : TL_STATES.error, events: [], partial: false, forId: id });
      });
    return () => {
      live = false;
    };
  }, [state.openId, timelineTick, navigate]);

  // Focus (review 2026-10-20). Opening moves focus to the panel itself, not
  // its close button: Enter there clicked the panel shut. Closing gives
  // focus back to what opened it, or, when the panel was walked with J / K
  // or that element is gone, to the cursor row's open control.
  const wasOpenRef = useRef(null);
  useEffect(() => {
    const was = wasOpenRef.current;
    wasOpenRef.current = state.openId;
    if (typeof document === "undefined") return;
    if (state.openId && !was) {
      const active = document.activeElement;
      openerRef.current = active && active !== document.body && !(panelRef.current && panelRef.current.contains(active)) ? active : null;
      if (panelRef.current) panelRef.current.focus({ preventScroll: true });
      return;
    }
    if (state.openId && was && state.openId !== was) {
      // Walked to another pair: the opener no longer names the open one.
      openerRef.current = null;
      return;
    }
    if (!state.openId && was) {
      const opener = openerRef.current;
      openerRef.current = null;
      const tryFocus = (el) => {
        if (!el || !el.isConnected || typeof el.focus !== "function") return false;
        el.focus({ preventScroll: true });
        return document.activeElement === el;
      };
      if (tryFocus(opener)) return;
      const rowEl = rowRefs.current[state.cursor];
      if (tryFocus(rowEl && rowEl.querySelector(".ib-open-btn"))) return;
      // Phones: the desktop grid is hidden, so its button takes no focus.
      tryFocus(document.querySelectorAll(".ib-phone .ib-pcard")[state.cursor]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.openId]);

  // ---- Keys: J / K move, Enter opens, / searches, Esc closes ----------------

  const keyRef = useRef({});
  keyRef.current = { visibleIds, openId: state.openId };
  useEffect(() => {
    const onKey = (e) => {
      if (dialogOpen()) return;
      const act = boardKeyAction(e);
      if (!act) return;
      const { visibleIds: ids, openId } = keyRef.current;
      if (act === "down" || act === "up") {
        e.preventDefault();
        dispatch({ type: "move", delta: act === "down" ? 1 : -1, ids });
      } else if (act === "open") {
        e.preventDefault();
        dispatch({ type: "openCursor", ids });
      } else if (act === "search") {
        e.preventDefault();
        if (searchRef.current) searchRef.current.focus();
      } else if (act === "close" && openId) {
        e.preventDefault();
        dispatch({ type: "close" });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const el = rowRefs.current[state.cursor];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [state.cursor]);

  // ---- Actions --------------------------------------------------------------

  const failText = (err, fallback) =>
    (err && err.response && err.response.data && err.response.data.message) || fallback;

  const runPairAction = async (row, closing) => {
    if (!row || !canActOn(row)) return;
    if (!window.confirm(closing ? CLOSE_CONFIRM : HONEST_CONFIRM)) return;
    setBusy(true);
    try {
      const res = closing ? await closePairKindly(row._id) : await sendHonestQuestion(row._id);
      addNotice("done", (res && res.data && res.data.message) || "Done.");
      setActedOn({ id: String(row._id), closing });
      reload();
      setTimelineTick((t) => t + 1);
    } catch (err) {
      if (err && err.response && err.response.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      addNotice("error", failText(err, "The action did not complete."));
    } finally {
      setBusy(false);
    }
  };

  // Ticked rows: only ever rows on screen (the reducer guarantees it).
  const selectedRows = state.selected.map((id) => rowById.get(id)).filter(Boolean);
  const eligibleRows = selectedRows.filter(canActOn);

  const runBulkAction = async (closing) => {
    if (!eligibleRows.length) {
      addNotice("info", "None of the selected introductions is a live pair of real members, so there is nothing to send.");
      return;
    }
    const n = eligibleRows.length;
    const skipped = selectedRows.length - n;
    const pairs = `${n} pair${n === 1 ? "" : "s"}`;
    const text = closing
      ? `Close ${pairs} kindly? Both members of each are told, and both go back into rotation. The honest question is sent first where nobody has been prompted yet.`
      : `Send the honest question to the quiet member of ${pairs}? One prompt per member per pair, ever.`;
    const skipNote = skipped ? ` ${skipped} selected ${skipped === 1 ? "is" : "are"} not a live pair of real members and will be skipped.` : "";
    if (!window.confirm(text + skipNote)) return;
    setBusy(true);
    const results = [];
    for (const row of eligibleRows) {
      try {
        // One at a time: each runs the Promise's own rails for one pair.
        // eslint-disable-next-line no-await-in-loop
        const res = closing ? await closePairKindly(row._id) : await sendHonestQuestion(row._id);
        results.push({ ok: true, message: res && res.data && res.data.message });
      } catch (err) {
        if (err && err.response && err.response.status === 401) {
          setBusy(false);
          handleUnauthorized(navigate);
          return;
        }
        results.push({ ok: false, message: failText(err, "") });
      }
    }
    setBusy(false);
    if (state.openId) {
      const k = eligibleRows.findIndex((r) => String(r._id) === state.openId);
      if (k >= 0 && results[k] && results[k].ok) setActedOn({ id: state.openId, closing });
    }
    addNotice(results.every((r) => r.ok) ? "done" : "error", bulkResultText(results));
    dispatch({ type: "clearSelection" });
    reload();
    setTimelineTick((t) => t + 1);
  };

  const deleteOne = async (row) => {
    if (!row) return;
    const confirmed = await showConfirmationDialog(
      "This will permanently delete this introduction record. Nobody is told.",
      "Delete record",
      "Cancel"
    );
    if (!confirmed.isConfirmed) return;
    setBusy(true);
    try {
      await deleteCompatibility(row._id);
      addNotice("done", "The record was deleted.");
      dispatch({ type: "close" });
      reload();
    } catch (err) {
      if (err && err.response && err.response.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      addNotice("error", failText(err, "The record was not deleted."));
    } finally {
      setBusy(false);
    }
  };

  const deleteSelected = async () => {
    const ids = state.selected.filter((id) => visibleIds.includes(id));
    if (!ids.length) return;
    const confirmed = await showConfirmationDialog(
      `This will permanently delete ${ids.length} selected introduction record${ids.length === 1 ? "" : "s"}. Nobody is told.`,
      "Delete selected",
      "Cancel"
    );
    if (!confirmed.isConfirmed) return;
    setBusy(true);
    try {
      await deleteCompatibilityBulk({ ids });
      addNotice("done", `${ids.length} record${ids.length === 1 ? " was" : "s were"} deleted.`);
      dispatch({ type: "clearSelection" });
      if (state.openId && ids.includes(state.openId)) dispatch({ type: "close" });
      reload();
    } catch (err) {
      if (err && err.response && err.response.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      addNotice("error", failText(err, "The records were not deleted."));
    } finally {
      setBusy(false);
    }
  };

  // ---- Bits of the page -------------------------------------------------------

  const tabs = tabsModel(meta.tabCounts, state.view);
  const strip = stageStripModel(meta.stageCounts, state.stage, { off: stripOff, nextRunAt, now });
  const pills = headerPills({ dealing: meta.dealing, promise: meta.promise }, now);
  const reverse = reverseModel(state);
  const sortOptions = sortOptionsFor(state.view, {
    legacyServer,
    reversedSort: reverse && reverse.on ? state.sort : "",
  });
  const headSort = (column) => headerSortModel(state, column, { legacyServer });
  const onHeaderSort = (sort) => dispatch({ type: "headerSort", sort });
  const allTicked = visibleIds.length > 0 && visibleIds.every((id) => state.selected.includes(id));
  const someTicked = state.selected.length > 0 && !allTicked;
  const headCheckRef = useRef(null);
  useEffect(() => {
    if (headCheckRef.current) headCheckRef.current.indeterminate = someTicked;
  }, [someTicked]);

  const memberName = (() => {
    if (!state.member) return "";
    for (const r of rows) {
      if (sideIdOf(r, "a") === state.member) return firstNameOf(r, "a");
      if (sideIdOf(r, "b") === state.member) return firstNameOf(r, "b");
    }
    return "";
  })();

  const openAt = (id, index) => dispatch({ type: "open", id, index });
  const goMember = (e, id) => {
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) return;
    if (e) e.preventDefault();
    dispatch({ type: "close" });
    dispatch({ type: "member", member: id });
  };
  const goTo = (e, href) => {
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) return;
    if (e) e.preventDefault();
    navigate(href);
  };

  const firstShown = meta.total ? (state.page - 1) * state.limit + 1 : 0;
  const lastShown = Math.min(state.page * state.limit, meta.total);
  const pageWindow = [];
  for (let p = Math.max(1, state.page - 2); p <= Math.min(totalPages, state.page + 2); p += 1) pageWindow.push(p);

  const personLine = (p) => (
    <span className="ib-person">
      <span
        className={`ib-dot ib-dot-${p.tier ? p.tier.key : "none"}`}
        title={p.tier ? `${p.tier.label}: ${p.tier.help}` : "Show-up tier not sent"}
      />
      <span className={`ib-name${p.waited ? " is-waited" : ""}`}>{p.name}</span>
      {p.waited && <span className="ib-waiting-tag">waiting</span>}
      {p.pile > 0 && (
        <span className="ib-pile" title={`Holding ${p.pile} untouched introduction${p.pile === 1 ? "" : "s"}`}>
          {p.pile}
        </span>
      )}
    </span>
  );

  const journeyLine = (p) => (
    <span className="ib-journey-line">
      <span className="ib-segs" title={p.vaultTitle}>
        {p.segs.map((s, k) => (
          <span key={k} className={`ib-seg ib-seg-${s}`} />
        ))}
      </span>
      <span
        className={`ib-jdot${p.tap === "yes" ? " ib-jdot-tap" : p.tap === "no" ? " ib-jdot-open" : ""}`}
        title={p.tapTitle}
      />
      <span
        className={`ib-jdot${p.hello === "yes" ? " ib-jdot-hello" : p.hello === "no" ? " ib-jdot-open" : ""}`}
        title={p.helloTitle}
      />
      <span className={`ib-wrote${p.wrote === 0 ? " is-zero" : ""}`} title={p.wroteTitle}>
        {p.wroteText}
      </span>
    </span>
  );

  // Nothing on screen: a failed load says so with Try again, never a false
  // "No live introductions right now" (review 2026-10-20).
  const emptyBlock = loadError ? (
    <div className="ib-empty">
      The introductions did not load.{" "}
      <button type="button" className="ib-btn ib-btn-plain" onClick={reload}>
        Try again
      </button>
    </div>
  ) : (
    <div className="ib-empty">{emptyStateText(state)}</div>
  );

  const skeletonRows = Array.from({ length: 6 }, (_, k) => (
    <div key={`sk${k}`} className="ib-row" aria-hidden="true" style={{ cursor: "default" }}>
      <span />
      <span className="ib-pair">
        <span className="ib-skel" style={{ width: 90 }} />
        <span className="ib-skel" style={{ width: 70 }} />
      </span>
      <span className="ib-journey">
        <span className="ib-skel" style={{ width: 150 }} />
        <span className="ib-skel" style={{ width: 150 }} />
      </span>
      <span className="ib-waiting">
        <span className="ib-skel" style={{ width: "60%", height: 18 }} />
        <span className="ib-skel" style={{ width: "80%" }} />
      </span>
      <span className="ib-dealt">
        <span className="ib-skel" style={{ width: 100 }} />
        <span className="ib-skel" style={{ width: 160 }} />
      </span>
      <span className="ib-dealt">
        <span className="ib-skel" style={{ width: 80 }} />
      </span>
      <span />
    </div>
  ));

  return (
    <div className="ib">
      {/* At phone width the open panel is a full-screen sheet: the page
          behind it is inert, so Tab stays in the sheet (review 2026-10-20). */}
      <div className="ib-main" inert={phone && panel ? "" : undefined}>
      <header className="ib-header">
        <div>
          <h1 className="ib-title">Introductions</h1>
          <p className="ib-subtitle">Who every introduction is waiting on, and what happens next. Times are Eastern.</p>
        </div>
        {pills.length > 0 && (
          <div className="ib-pills">
            {pills.map((p) => (
              <span key={p.key} className={`ib-pill ib-pill-${p.tone}`} title={p.title}>
                {p.text ? (
                  p.text
                ) : (
                  <>
                    {p.before} <strong>{p.strong}</strong>
                    {p.after ? ` ${p.after}` : ""}
                  </>
                )}
              </span>
            ))}
          </div>
        )}
      </header>

      {notices.map((n) => (
        <div key={n.id} className={`ib-notice ib-notice-${n.kind}`} role={n.kind === "error" ? "alert" : "status"}>
          <span>{n.text}</span>
          <button type="button" onClick={() => dismissNotice(n.id)}>
            Dismiss
          </button>
        </div>
      ))}

      <div className="ib-toolbar">
        <div className="ib-tabs" role="tablist" aria-label="Which introductions">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              className="ib-tab"
              aria-selected={t.on}
              onClick={() => dispatch({ type: "view", view: t.key })}
            >
              {t.label}
              {t.count !== null && <span className="ib-tab-count">{t.count}</span>}
            </button>
          ))}
        </div>

        <label className="ib-search" htmlFor="ib-search">
          <span className="ib-search-icon">
            <SearchIcon />
          </span>
          <input
            id="ib-search"
            ref={searchRef}
            type="search"
            placeholder="Search names or emails"
            aria-label="Search names or emails"
            value={state.search}
            onChange={(e) => dispatch({ type: "search", search: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Escape") e.currentTarget.blur();
            }}
          />
          {!state.search && <span className="ib-kbd">/</span>}
        </label>

        <div
          className="ib-dates"
          title="Eastern calendar days, on the day each introduction was dealt. A pair dealt again counts from its newest deal."
        >
          <DatePicker
            selectsRange
            startDate={dateOf(state.from)}
            endDate={dateOf(state.to)}
            onChange={(range) => {
              const [start, end] = Array.isArray(range) ? range : [null, null];
              dispatch({ type: "dates", from: ymdOf(start), to: ymdOf(end) });
            }}
            isClearable
            placeholderText="Dealt: any date"
            dateFormat="MMM d, yyyy"
            ariaLabelledBy="ib-dates-label"
          />
          <span id="ib-dates-label" hidden>
            Dealt between these dates
          </span>
        </div>

        <label className="ib-sort" htmlFor="ib-sort">
          Sort
          <select
            id="ib-sort"
            className="ib-control"
            value={state.sort}
            onChange={(e) => dispatch({ type: "sort", sort: e.target.value })}
          >
            {sortOptions.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        {reverse && (
          <button
            type="button"
            className="ib-control ib-reverse"
            aria-pressed={reverse.on}
            aria-label={reverse.label}
            title={reverse.label}
            onClick={() => dispatch({ type: "reverse" })}
          >
            <ReverseIcon />
          </button>
        )}

        <select
          className="ib-control"
          aria-label="Rows per page"
          value={state.limit}
          onChange={(e) => dispatch({ type: "limit", limit: Number(e.target.value) })}
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n} per page
            </option>
          ))}
        </select>
      </div>

      {(state.status || state.member) && (
        <div className="ib-chips">
          {state.status && (
            <span className="ib-chip">
              {statusChipText(state.status)}
              <button type="button" aria-label="Remove the filter" onClick={() => dispatch({ type: "clearStatus" })}>
                <CloseIcon />
              </button>
            </span>
          )}
          {state.member && (
            <span className="ib-chip">
              {memberName ? `Only ${memberName}'s introductions` : "Only one member's introductions"}
              {legacyServer ? " (one side only on this server)" : ""}
              <button type="button" aria-label="Show everyone" onClick={() => dispatch({ type: "clearMember" })}>
                <CloseIcon />
              </button>
            </span>
          )}
        </div>
      )}

      {state.view === "live" && (
        <section className="ib-strip" aria-label="Where live introductions are">
          <div className="ib-stages">
            {strip.stages.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`ib-stage ib-tone-${s.tone}`}
                style={{ flexGrow: s.grow }}
                aria-pressed={s.on}
                disabled={!!strip.off}
                onClick={() => dispatch({ type: "stage", stage: s.key })}
              >
                <span className="ib-stage-top">
                  {s.count !== null && <span className="ib-count">{s.count}</span>}
                  <span className="ib-stage-label">{s.label}</span>
                </span>
                <span className="ib-bar" />
              </button>
            ))}
          </div>
          <div className="ib-alerts">
            {strip.alerts.map((a) => (
              <button
                key={a.key}
                type="button"
                className={`ib-alert ib-alert-${a.tone}`}
                aria-pressed={a.on}
                disabled={!!strip.off}
                onClick={() => dispatch({ type: "stage", stage: a.key })}
              >
                {a.count !== null && <span className="ib-count">{a.count}</span>}
                <span className="ib-alert-label">
                  <span className="ib-alert-title">{a.label}</span>
                  <span className="ib-alert-sub">{a.sub}</span>
                </span>
              </button>
            ))}
          </div>
          {strip.off && (
            <p className="ib-strip-off" role="status">
              {strip.off}
            </p>
          )}
        </section>
      )}

      <section className="ib-list" aria-label="Introductions list" aria-busy={loading}>
        {/* Wide screens: the grid. */}
        {/* No table roles (review 2026-10-20): the grid is plain layout, and
            each row's "Open {pair}" button carries its meaning. */}
        <div className="ib-desktop ib-scroll">
          <div className="ib-grid">
            <div className="ib-head">
              <span className="ib-check">
                {canSelect && (
                  <input
                    ref={headCheckRef}
                    type="checkbox"
                    aria-label="Select every row shown"
                    checked={allTicked}
                    disabled={!visibleIds.length}
                    onChange={() => dispatch({ type: "toggleAll", visibleIds })}
                  />
                )}
              </span>
              <SortTitle sort={headSort("pair")} onSort={onHeaderSort}>
                Pair
              </SortTitle>
              <span className="ib-journey-head">
                <span>Vault</span>
                <span>Reveal</span>
                <span>Hello</span>
                <span>Wrote</span>
              </span>
              <SortTitle sort={headSort("waiting")} onSort={onHeaderSort}>
                Waiting on
              </SortTitle>
              <SortTitle sort={headSort("next")} onSort={onHeaderSort}>
                Next
              </SortTitle>
              <SortTitle sort={headSort("dealt")} onSort={onHeaderSort}>
                Dealt
              </SortTitle>
              <span />
            </div>

            {loading && !rows.length
              ? skeletonRows
              : models.map((m, i) => (
                  <div
                    key={m.id}
                    ref={(el) => {
                      rowRefs.current[i] = el;
                    }}
                    className={`ib-row${state.openId === m.id ? " is-open" : ""}${state.cursor === i ? " is-cursor" : ""}`}
                    onClick={() => openAt(m.id, i)}
                    style={loading ? { opacity: 0.6 } : undefined}
                  >
                    <span className="ib-check" onClick={(e) => e.stopPropagation()}>
                      {canSelect && (
                        <input
                          type="checkbox"
                          aria-label={`Select ${m.label}`}
                          checked={state.selected.includes(m.id)}
                          onChange={() => dispatch({ type: "toggleRow", id: m.id, visibleIds })}
                        />
                      )}
                    </span>
                    <button
                      type="button"
                      className="ib-unbutton ib-pair"
                      aria-label={`Open ${m.label}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openAt(m.id, i);
                      }}
                    >
                      {personLine(m.a)}
                      {personLine(m.b)}
                    </button>
                    <span className="ib-journey">
                      {journeyLine(m.a)}
                      {journeyLine(m.b)}
                    </span>
                    <span className="ib-waiting">
                      <span className="ib-badge-line">
                        <span className={`ib-badge ib-tone-bg-${m.tone}`} title={m.headline}>
                          {m.headline}
                        </span>
                        {m.again && (
                          <span className="ib-tag ib-tag-again" title={m.againText}>
                            again
                          </span>
                        )}
                        {m.test && <span className="ib-tag ib-tag-test">test</span>}
                      </span>
                      {m.detail && (
                        <span className="ib-detail" title={m.detail}>
                          {m.detail}
                        </span>
                      )}
                    </span>
                    <span className={`ib-next is-${m.next.tone}`}>
                      {m.next.when && <ClockIcon className="ib-clock" />}
                      <span className="ib-next-text">
                        <span className="ib-next-when">{m.next.when}</span>
                        {m.next.what && (
                          <span className="ib-next-what" title={m.next.what}>
                            {m.next.what}
                          </span>
                        )}
                      </span>
                    </span>
                    <span
                      className="ib-dealt"
                      title={[m.dealt.title, m.dealt.common ? `${m.dealt.common} in common` : ""].filter(Boolean).join(". ")}
                    >
                      <span className="ib-dealt-when">{m.dealt.when}</span>
                      <span className="ib-sub">{[m.dealt.ago, m.dealt.common].filter(Boolean).join(" · ")}</span>
                    </span>
                    <button
                      type="button"
                      className="ib-open-btn"
                      aria-label={`Open ${m.label}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openAt(m.id, i);
                      }}
                    >
                      <ChevronIcon />
                    </button>
                  </div>
                ))}

            {!loading && !rows.length && emptyBlock}
          </div>
        </div>

        {/* Phones (under 768px): one compact card per introduction, no
            checkboxes (as in the approved Phone mock-up). */}
        <div className="ib-phone">
          {loading && !rows.length && <div className="ib-empty">Loading introductions</div>}
          {!loading && !rows.length && emptyBlock}
          {models.map((m, i) => (
            <button
              key={m.id}
              type="button"
              className={`ib-pcard${state.openId === m.id ? " is-open" : ""}`}
              onClick={() => openAt(m.id, i)}
              aria-label={`Open ${m.label}`}
            >
              <span className="ib-pcard-top">
                <span className="ib-pcard-names">
                  <span className={`ib-dot ib-dot-${m.a.tier ? m.a.tier.key : "none"}`} />
                  <span className={`ib-name${m.a.waited ? " is-waited" : ""}`}>{m.a.name}</span>
                  <span className="ib-and">and</span>
                  <span className={`ib-dot ib-dot-${m.b.tier ? m.b.tier.key : "none"}`} />
                  <span className={`ib-name${m.b.waited ? " is-waited" : ""}`}>{m.b.name}</span>
                </span>
                <span className="ib-sub">{m.dealt.when}</span>
              </span>
              <span className={`ib-badge ib-tone-bg-${m.tone}`}>{m.headline}</span>
              {m.detail && <span className="ib-detail">{m.detail}</span>}
              {m.next.when && (
                <span className={`ib-pcard-next${m.next.urgent ? " is-urgent" : ""}`}>
                  <ClockIcon />
                  {[m.next.when, m.next.what].filter(Boolean).join(" · ")}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="ib-footer">
          <span>
            {meta.total
              ? `Showing ${firstShown} to ${lastShown} of ${meta.total}`
              : loading
              ? "Loading"
              : loadError && !rows.length
              ? "Not loaded"
              : "Nothing to show"}
          </span>
          <span className="ib-keys">J / K move · Enter open · / search · Esc close</span>
          {totalPages > 1 && (
            <ul className="ib-pager" aria-label="Pages">
              <li>
                <button
                  type="button"
                  className="ib-page"
                  aria-label="Previous page"
                  disabled={state.page <= 1}
                  onClick={() => dispatch({ type: "page", page: state.page - 1 })}
                >
                  <ChevronIcon left />
                </button>
              </li>
              {pageWindow.map((p) => (
                <li key={p}>
                  <button
                    type="button"
                    className="ib-page"
                    aria-current={p === state.page ? "page" : undefined}
                    onClick={() => dispatch({ type: "page", page: p })}
                  >
                    {p}
                  </button>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  className="ib-page"
                  aria-label="Next page"
                  disabled={state.page >= totalPages}
                  onClick={() => dispatch({ type: "page", page: state.page + 1 })}
                >
                  <ChevronIcon />
                </button>
              </li>
            </ul>
          )}
        </div>
      </section>

      {state.selected.length > 0 && (
        <div className="ib-bulk ib-desktop" role="region" aria-label="Selected introductions">
          <span className="ib-bulk-count">{state.selected.length} selected</span>
          {canEdit && (
            <>
              <button type="button" className="ib-btn" disabled={busy} onClick={() => runBulkAction(false)}>
                Send the honest question
              </button>
              <button type="button" className="ib-btn" disabled={busy} onClick={() => runBulkAction(true)}>
                Close kindly
              </button>
            </>
          )}
          {canDelete && (
            <>
              <span className="ib-sep" />
              <button type="button" className="ib-btn ib-btn-danger" disabled={busy} onClick={deleteSelected}>
                Delete records
              </button>
            </>
          )}
          <button type="button" className="ib-btn ib-btn-plain" onClick={() => dispatch({ type: "clearSelection" })}>
            Clear
          </button>
        </div>
      )}
      </div>

      {panel && (
        <aside
          ref={panelRef}
          className="ib-panel"
          role="dialog"
          aria-modal={phone ? "true" : undefined}
          aria-labelledby="ib-panel-title"
          tabIndex={-1}
        >
          <div className="ib-panel-top">
            <div>
              <h2 id="ib-panel-title" className="ib-panel-title">
                {panel.label}
              </h2>
              <span className="ib-panel-sub">{panel.sub}</span>
            </div>
            <button
              type="button"
              className="ib-icon-btn"
              aria-label="Close the panel"
              onClick={() => dispatch({ type: "close" })}
            >
              <CloseIcon />
            </button>
          </div>

          <div className={`ib-headline-block ib-tone-bg-${panel.tone}`}>
            <span className="ib-hl">{panel.headline}</span>
            {panel.lines.map((line, k) => (
              <span key={k} className="ib-line">
                {line}
              </span>
            ))}
            {panel.next && (
              <span className="ib-headline-next">
                <ClockIcon />
                {[panel.next.when, panel.next.what].filter(Boolean).join(" · ")}
              </span>
            )}
          </div>

          <div className="ib-people">
            {panel.people.map((p, k) => (
              <div key={p.id || k} className="ib-card">
                <span className="ib-card-name">
                  <strong>{p.name}</strong>
                  {p.tier && (
                    <span className={`ib-tier ib-tier-${p.tier.key}`} title={p.tier.help}>
                      {p.tier.label}
                    </span>
                  )}
                </span>
                {p.email && (
                  <span className="ib-email" title={p.email}>
                    {p.email}
                  </span>
                )}
                <span>{p.vaultText}</span>
                {p.inApp && <span>{p.inApp}</span>}
                {p.stageSeen && <span className="ib-muted">{p.stageSeen}</span>}
                {p.tier && <span className="ib-muted">{p.tier.help}</span>}
                {p.untouched && <span style={{ color: "var(--ib-red-text)" }}>{p.untouched}</span>}
                {(p.appVersion || p.onBreak) && (
                  <span className="ib-muted">{[p.appVersion, p.onBreak].filter(Boolean).join(" · ")}</span>
                )}
                {p.id && (
                  <span>
                    <a href={p.memberHref} onClick={(e) => goTo(e, p.memberHref)}>
                      Member page
                    </a>
                    {" · "}
                    <a href={`/compatibility-control?view=all&member=${p.id}`} onClick={(e) => goMember(e, p.id)}>
                      All their introductions
                    </a>
                  </span>
                )}
              </div>
            ))}
          </div>

          {panel.prompts.length > 0 && (
            <div>
              <h3 className="ib-section-title">Promise prompts</h3>
              {panel.prompts.map((x, k) => (
                <div key={k} className="ib-tl-text">
                  Reminder to {x.who}
                  {x.when ? `, ${x.when}` : ""}
                </div>
              ))}
            </div>
          )}

          <div>
            <h3 className="ib-section-title">Timeline</h3>
            {timeline.forId === panel.id && timeline.status === TL_STATES.loading && (
              <span className="ib-muted">Loading the timeline</span>
            )}
            {timeline.forId === panel.id && timeline.status === TL_STATES.missing && (
              <span className="ib-muted">Timeline not available yet</span>
            )}
            {timeline.forId === panel.id && timeline.status === TL_STATES.error && (
              <span className="ib-muted">
                The timeline did not load.{" "}
                <button type="button" className="ib-btn ib-btn-plain" onClick={() => setTimelineTick((t) => t + 1)}>
                  Try again
                </button>
              </span>
            )}
            {timeline.forId === panel.id &&
              timeline.status === TL_STATES.ok &&
              (() => {
                const items = timelineModel(timeline.events, now);
                const retry = (
                  <button type="button" className="ib-btn ib-btn-plain" onClick={() => setTimelineTick((t) => t + 1)}>
                    Try again
                  </button>
                );
                // partial: a source failed to read (review 2026-10-20).
                if (!items.length && timeline.partial) {
                  return (
                    <span className="ib-muted">
                      The timeline could not be read. {retry}
                    </span>
                  );
                }
                return (
                  <>
                    {items.map((ev, k) => (
                      <div key={k} className={`ib-timeline-item${ev.future ? " is-future" : ""}`}>
                        <span className="ib-tl-dot" />
                        <span className="ib-tl-when">{ev.when}</span>
                        <span className="ib-tl-text">{ev.text}</span>
                      </div>
                    ))}
                    {!items.length && <span className="ib-muted">Nothing recorded yet</span>}
                    {timeline.partial && (
                      <span className="ib-muted">
                        Some of this story could not be read. {retry}
                      </span>
                    )}
                  </>
                );
              })()}
          </div>

          {canEdit && panel.actionable && panelStale && (
            <span className="ib-muted">
              {actedOn.closing
                ? "Closed kindly. It has left this list; find it on the Ended tab."
                : "Done. It has left this list; find it again from the tabs."}
            </span>
          )}
          {canEdit && panel.actionable && !panelStale && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {/* Off while the list reloads: the row may still be the copy
                  from before the last action (review 2026-10-20). */}
              <button type="button" className="ib-action" disabled={busy || loading} onClick={() => runPairAction(openRow, false)}>
                <strong>Send the honest question</strong>
                <span>One prompt to the quiet member, once ever per pair</span>
              </button>
              <button type="button" className="ib-action" disabled={busy || loading} onClick={() => runPairAction(openRow, true)}>
                <strong>Close kindly</strong>
                <span>Both are told, both go back into rotation</span>
              </button>
            </div>
          )}
          {canEdit && !panel.actionable && !panel.ended && openRow && openRow.isTestPair && (
            <span className="ib-muted">Test pair: the Promise never acts on it, so its actions are off here.</span>
          )}

          <div className="ib-links">
            {canView && (
              <>
                <a
                  className="ib-link-btn"
                  href={`/compatibility-control/view/${panel.id}?tab=chat`}
                  onClick={(e) => goTo(e, `/compatibility-control/view/${panel.id}?tab=chat`)}
                >
                  Chat transcript
                </a>
                <a
                  className="ib-link-btn"
                  href={`/compatibility-control/view/${panel.id}?tab=vault-unlock`}
                  onClick={(e) => goTo(e, `/compatibility-control/view/${panel.id}?tab=vault-unlock`)}
                >
                  Vault and reveal details
                </a>
              </>
            )}
            {canEdit && (
              <a
                className="ib-link-btn"
                href={`/compatibility-control/edit/${panel.id}`}
                onClick={(e) => goTo(e, `/compatibility-control/edit/${panel.id}`)}
              >
                Edit status
              </a>
            )}
          </div>

          {canDelete && (
            <details className="ib-advanced">
              <summary>Advanced</summary>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 10 }}>
                <button
                  type="button"
                  className="ib-btn ib-btn-danger"
                  style={{ textAlign: "left" }}
                  disabled={busy}
                  onClick={() => deleteOne(openRow)}
                >
                  Delete this record
                </button>
                <span className="ib-muted" style={{ fontSize: 12 }}>
                  Removes the row outright. Nobody is told. Close kindly is almost always the right choice.
                </span>
              </div>
            </details>
          )}
        </aside>
      )}
    </div>
  );
};

export default IntroductionsBoard;
